import { SortOption } from "./MobileSortSideSheet";
import { ColumnDef } from "@tanstack/react-table";

/**
 * Defines the type of data being sorted to determine appropriate display text
 * - text: Alphabetical data (A to Z / Z to A)
 * - numeric: Number data (Low to High / High to Low)
 * - date: Date/time data (Oldest First / Newest First)
 * - count: Count/quantity data (Least to Most / Most to Least)
 */
export type SortFieldType = "text" | "numeric" | "date" | "count";

/**
 * Default display text patterns for different data types
 */
const defaultSortDisplayText: Record<SortFieldType, { asc: string; desc: string }> = {
  text: { asc: "A to Z", desc: "Z to A" },
  numeric: { asc: "Low to High", desc: "High to Low" },
  date: { asc: "Oldest First", desc: "Newest First" },
  count: { asc: "Least to Most", desc: "Most to Least" }
};

/**
 * Creates an array of sort options by extracting sort metadata from columns array.
 *
 * This function is used internally by the Table component to automatically generate
 * mobile sort options from column definitions.
 *
 * @param columns - TanStack Table column definitions with optional meta.sortType
 * @returns Array of SortOption objects suitable for MobileSortSideSheet
 *
 * @example
 * ```tsx
 * // The Table component automatically extracts sortType from column meta:
 * const columns = [
 *   {
 *     accessorKey: "title",
 *     header: "Event Name",
 *     meta: { sortType: "text" }  // ✅ Auto-enables sorting (desktop + mobile)
 *   },
 *   {
 *     accessorKey: "date",
 *     header: "Date",
 *     meta: { sortType: "date" }  // ✅ Auto-enables sorting (desktop + mobile)
 *   },
 *   {
 *     accessorKey: "status",
 *     header: "Status"
 *     // ❌ No meta.sortType = not sortable
 *   }
 * ];
 * // Note: enableSorting is automatically set to true when sortType is provided
 * ```
 */
export function createSortOptionsFromColumns<T>(columns: ColumnDef<T, any>[]): SortOption[] {
  const sortOptions: SortOption[] = [];

  for (const column of columns) {
    const sortType = column.meta?.sortType as SortFieldType | undefined;

    if (sortType) {
      const columnId = column.id || ("accessorKey" in column ? String(column.accessorKey) : undefined);

      if (columnId) {
        const displayText = defaultSortDisplayText[sortType];
        const sortLabel = column.meta?.sortLabel as string | undefined;
        const label = sortLabel || (typeof column.header === "string" && column.header ? column.header : columnId);

        sortOptions.push({
          id: columnId,
          label,
          desc: false,
          display: displayText.asc
        });

        sortOptions.push({
          id: columnId,
          label,
          desc: true,
          display: displayText.desc
        });
      }
    }
  }

  return sortOptions;
}
